import { FC, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink } from '@react-pdf/renderer';
import PDFLogo from '@/components/pdf/PDFLogo';
import supabaseClient from '@/api/supabaseClient.ts';
import { getLevelName, getLevelColor } from '@/utils/assessmentUtils';
import { Header } from '../components/shared/Header/Header';
import longAssessments from '@/_constants/LongAssessments.json';
import DownloadIcon from '@/components/icons/DownloadIcon';
import PrintIcon from '@/components/icons/PrintIcon';

interface LongAssessmentLevel {
  discovery: string;
  foundational: string;
  emerging: string;
  developing: string;
  advancing: string;
  thriving: string;
}

interface LongAssessmentDomain {
  [subdomain: string]: LongAssessmentLevel;
}

interface LongAssessments {
  [domain: string]: LongAssessmentDomain;
}

interface SubdomainData {
  name: string;
  discovery_description?: string;
  foundational_description?: string;
  emerging_description?: string;
  developing_description?: string;
  advancing_description?: string;
  thriving_description?: string;
  not_applicable_description?: string;
}

interface ResponseData {
  selected_level: number;
  subdomains: SubdomainData;
}

type AnswerRow = {
  subdomainName: string;
  selectedLevel: number;
  answerText: string;
};

const pdfStyles = StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    padding: 40,
    paddingTop: 70,
    paddingBottom: 50,
    fontFamily: 'Helvetica',
  },
  headerBar: {
    position: 'absolute',
    top: 20,
    left: 40,
    right: 40,
    height: 30,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  logo: {
    width: 90,
    height: 22,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 9,
    color: '#6b7280',
  },
  container: {
    flex: 1,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 30,
    textAlign: 'left',
    color: '#000000',
  },
  table: {
    width: '100%',
    marginTop: 20,
  },
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    paddingBottom: 10,
    marginBottom: 10,
  },
  tableHeaderCell: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#000000',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#cccccc',
    paddingVertical: 10,
  },
  tableCell: {
    fontSize: 10,
    color: '#000000',
  },
  subdomainCell: {
    width: '25%',
  },
  levelCell: {
    width: '25%',
  },
  answerCell: {
    width: '50%',
  },
  levelBadge: {
    borderRadius: 4,
    paddingVertical: 3,
    paddingHorizontal: 6,
    alignSelf: 'flex-start',
  },
  levelBadgeText: {
    fontSize: 10,
    color: '#ffffff',
  },
});

const getLevelBadgeStyle = (level: number): React.CSSProperties => ({
  backgroundColor: getLevelColor(level),
  color: '#ffffff'
});

const AssessmentAnswersPDFDocument: FC<{ assessmentName: string; answers: AnswerRow[] }> = ({ assessmentName, answers }) => {
  const { t } = useTranslation();
  
  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.headerBar}>
          <PDFLogo width={90} height={22} />
        </View>
        <View style={pdfStyles.container}>
          <Text style={pdfStyles.title}>{assessmentName}</Text>
          
          <View style={pdfStyles.table}>
            <View style={pdfStyles.tableHeader}>
              <Text style={[pdfStyles.tableHeaderCell, pdfStyles.subdomainCell]}>
                {t('assessment.subdomain')}
              </Text>
              <Text style={[pdfStyles.tableHeaderCell, pdfStyles.levelCell]}>
                {t('assessment.selectedLevel')}
              </Text>
              <Text style={[pdfStyles.tableHeaderCell, pdfStyles.answerCell]}>
                {t('assessment.yourAnswer')}
              </Text>
            </View>

            {answers.map((row, idx) => (
              <View key={idx} style={pdfStyles.tableRow}>
                <Text style={[pdfStyles.tableCell, pdfStyles.subdomainCell]}>
                  {row.subdomainName}
                </Text>
                <View style={pdfStyles.levelCell}>
                  <View style={[pdfStyles.levelBadge, { backgroundColor: getLevelColor(row.selectedLevel) }]}>
                    <Text style={pdfStyles.levelBadgeText}>{getLevelName(row.selectedLevel)}</Text>
                  </View>
                </View>
                <Text style={[pdfStyles.tableCell, pdfStyles.answerCell]}>
                  {row.answerText || '-'}
                </Text>
              </View>
            ))}
          </View>
        </View>
        <Text style={pdfStyles.footer}>Copyright @ 2025 ViaTelos. All Rights Reserved.</Text>
      </Page>
    </Document>
  );
};

const AssessmentAnswersPage: FC = () => {
  const { t } = useTranslation();

  const { userAssessmentId } = useParams<{ userAssessmentId: string }>();

  const [assessmentName, setAssessmentName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [answers, setAnswers] = useState<AnswerRow[]>([]);

  const fetchAnswers = useCallback(async () => {
    try {
      setLoading(true);

      const { data: assessmentData, error: assessmentError } = await supabaseClient
        .from('user_assessments')
        .select('assessment_name')
        .eq('id', userAssessmentId)
        .maybeSingle();

      if (assessmentError || !assessmentData) {
        return;
      }

      setAssessmentName(assessmentData.assessment_name);

      const { data: responsesData, error: responsesError } = await supabaseClient
        .from('user_responses')
        .select(`
          selected_level,
          subdomains (
            id,
            name,
            discovery_description,
            foundational_description,
            emerging_description,
            developing_description,
            advancing_description,
            thriving_description,
            not_applicable_description,
            domains (
              id,
              name
            )
          )
        `)
        .eq('user_assessment_id', userAssessmentId);

      if (responsesError) {
        return;
      }

      const rows: AnswerRow[] = (responsesData as unknown as ResponseData[] || []).map((r) => {
        const sd = r.subdomains || {};
        const level = r.selected_level ?? 0;
        let answerText = '';
        switch (level) {
          case 1: answerText = sd.discovery_description || ''; break;
          case 2: answerText = sd.foundational_description || ''; break;
          case 3: answerText = sd.emerging_description || ''; break;
          case 4: answerText = sd.developing_description || ''; break;
          case 5: answerText = sd.advancing_description || ''; break;
          case 6: answerText = sd.thriving_description || ''; break;
          case 0: answerText = sd.not_applicable_description || (t('assessment.notApplicable') as string) || ''; break;
          default: answerText = ''; break;
        }
        return {
          subdomainName: sd?.name || '',
          selectedLevel: level,
          answerText,
        };
      });

      rows.sort((a, b) => a.subdomainName.localeCompare(b.subdomainName));

      setAnswers(rows);
    } finally {
      setLoading(false);
    }
  }, [userAssessmentId, t]);

  useEffect(() => {
    if (userAssessmentId) {
      fetchAnswers();
    }
  }, [userAssessmentId, fetchAnswers]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-semibold">{t('assessment.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl">
      <Header
        rightContent={
          <div className="flex flex-row gap-2">
            <PDFDownloadLink
              document={<AssessmentAnswersPDFDocument assessmentName={assessmentName} answers={answers} />}
              fileName={`${assessmentName.toLowerCase().replace(/\s+/g, '-')}-answers.pdf`}
              className="bg-gray-800 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-[#4c8025] transition-colors flex items-center justify-center gap-2"
            >
              {({ loading: pdfLoading }) => (
                <>
                  <DownloadIcon className="w-5 h-5" />
                  <span className="hidden sm:inline text-sm sm:text-base">
                    {pdfLoading ? t('recommendations.generating') : t('recommendations.downloadPDF')}
                  </span>
                </>
              )}
            </PDFDownloadLink>

            <button
              onClick={() => window.print()}
              className="bg-[#5E9E2E] text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-[#4c8025] transition-colors flex items-center justify-center gap-2"
              type="button"
            >
              <PrintIcon className="w-5 h-5" />
              <span className="hidden sm:inline text-sm sm:text-base">{t('print')}</span>
            </button>
          </div>
        }
      />

      <div className="pt-6">
        <div className="bg-white rounded-2xl shadow-sm">
          <div className="px-4 sm:px-6 py-3 border-b">
            <h2 className="text-base sm:text-lg font-semibold text-gray-800">
              {t('assessment.answers') || 'Answer Summary'}
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t('assessment.subdomain') || 'Subdomain'}
                  </th>
                  <th className="px-2 sm:px-4 md:px-6 py-3 text-left whitespace-nowrap text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('assessment.selectedLevel') || 'Selected Level'}
                  </th>
                  <th className="px-2 sm:px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {t('assessment.yourAnswer') || 'Your Answer'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {answers.map((row, idx) => {
                  const domainKey = Object.keys(longAssessments as LongAssessments).find(domain => 
                    Object.keys((longAssessments as LongAssessments)[domain]).find(subdomain => 
                      subdomain === row.subdomainName
                    )
                  );
                  
                  const longAssessmentText = domainKey && (longAssessments as LongAssessments)[domainKey][row.subdomainName]?.[getLevelName(row.selectedLevel).toLowerCase() as keyof LongAssessmentLevel];

                  return (
                    <tr key={`${row.subdomainName}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-2 sm:px-4 md:px-6 py-4 text-xs sm:text-sm text-gray-700 min-w-[120px] sm:min-w-0">
                        {row.subdomainName}
                      </td>
                      <td className="px-2 sm:px-4 md:px-6 py-4 whitespace-nowrap text-xs sm:text-sm min-w-[100px] sm:min-w-0">
                        <span
                          className="inline-flex justify-center items-center px-2 py-0.5 rounded-md text-xs sm:text-sm font-medium"
                          style={getLevelBadgeStyle(row.selectedLevel)}
                        >
                          {getLevelName(row.selectedLevel)}
                        </span>
                      </td>
                      <td 
                        className="px-2 sm:px-4 md:px-6 py-4 text-xs sm:text-sm text-gray-700 relative group cursor-pointer min-w-[200px] sm:min-w-0"
                      >
                        <div className="relative">
                          <div className="line-clamp-3 sm:line-clamp-none">
                            {row.answerText || '-'}
                          </div>
                          {longAssessmentText && (
                            <div className="hidden md:block opacity-0 group-hover:opacity-100 transition-opacity absolute z-[9999] bg-gray-900 text-white text-sm rounded-lg p-4 w-[500px] max-w-[90vw] shadow-xl -translate-x-1/2 left-[50%] top-[100%] mt-2">
                              {longAssessmentText}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {answers.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 sm:px-6 py-6 text-center text-sm text-gray-500">
                      {t('assessment.noAnswers') || 'No answers found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentAnswersPage;


